import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, extractError } from "@/lib/api";
import { toast } from "sonner";
import {
  MapPin, DollarSign, Calendar, Users, ArrowLeft, MessageSquare, Loader2, ShieldCheck,
} from "lucide-react";

const RESPITE = [
  ["traditional_respite", "Traditional respite"],
  ["overnight_support", "Overnight support"],
  ["travel_assistance", "Travel assistance"],
  ["party_buffers", "Party buffers"],
  ["community_support", "Community support"],
];

const FREQ_LABEL = {
  one_time: "One time",
  weekly: "Weekly",
  biweekly: "Every 2 weeks",
  monthly: "Monthly",
  occasional: "Occasional",
};

export default function SitterListingDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [listing, setListing] = useState(null);
  const [msg, setMsg] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get(`/family-listings/${id}`)
      .then(({ data }) => { if (alive) setListing(data); })
      .catch((e) => toast.error(extractError(e)));
    return () => { alive = false; };
  }, [id]);

  const sendMessage = async () => {
    if (!msg.trim()) return toast.error("Write a short intro message");
    setSending(true);
    try {
      await api.post("/messages", {
        to_user_id: listing.family_user_id,
        body: msg.trim(),
        listing_id: listing.id,
      });
      toast.success("Message sent — the family will see it in their inbox");
      setMsg("");
      navigate("/messages");
    } catch (e) { toast.error(extractError(e)); }
    finally { setSending(false); }
  };

  if (!listing) {
    return (
      <div className="min-h-screen bg-stone2-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-10 text-stone2-500">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone2-50" data-testid="sitter-listing-detail">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-8">
        <Link to="/listings" className="inline-flex items-center gap-1.5 text-sm text-sage-700 font-semibold mb-5 hover:text-sage-900">
          <ArrowLeft className="w-4 h-4" /> Back to listings
        </Link>

        <div className="grid lg:grid-cols-[1fr_360px] gap-6">
          <article className="paper-card p-6 sm:p-8">
            <div className="text-xs uppercase tracking-[0.2em] text-stone2-500">
              {FREQ_LABEL[listing.frequency] || listing.frequency}
              {listing.distance_miles !== undefined && <> · {listing.distance_miles} mi</>}
            </div>
            <h1 className="mt-2 font-heading font-bold text-sage-900 text-3xl leading-tight">{listing.title}</h1>

            <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-stone2-700">
              <span className="inline-flex items-center gap-1.5">
                <MapPin className="w-4 h-4 text-sage-700" />
                {[listing.city, listing.state].filter(Boolean).join(", ") || "Area"} · {listing.zip_code}
              </span>
              {listing.hourly_rate_offered && (
                <span className="inline-flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4 text-sage-700" /> ${listing.hourly_rate_offered}/hr offered
                </span>
              )}
              {listing.schedule_notes && (
                <span className="inline-flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-sage-700" /> {listing.schedule_notes}
                </span>
              )}
            </div>

            {listing.child_ages_summary && (
              <div className="mt-4 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3 text-sm text-sage-900 inline-flex items-center gap-2">
                <Users className="w-4 h-4" /> {listing.child_ages_summary}
              </div>
            )}

            <div className="prose prose-sm max-w-none mt-6 text-stone2-700 whitespace-pre-wrap leading-relaxed">
              {listing.description}
            </div>

            {(listing.respite_services_needed?.length > 0 || listing.required_certifications?.length > 0
              || listing.aba_required || listing.overnight_needed) && (
              <div className="mt-6 border-t border-stone2-200 pt-5">
                <h2 className="font-heading font-semibold text-sage-900 mb-2 text-sm uppercase tracking-wider">What they're looking for</h2>
                <div className="flex flex-wrap gap-1.5">
                  {(listing.respite_services_needed || []).map((s) => (
                    <span key={s} className="chip">{(RESPITE.find(([k]) => k === s) || [s, s])[1]}</span>
                  ))}
                  {(listing.required_certifications || []).map((c) => (
                    <span key={c} className="chip">{c}</span>
                  ))}
                  {listing.aba_required && <span className="chip-ochre">ABA required</span>}
                  {listing.overnight_needed && <span className="chip-ochre">Overnight</span>}
                </div>
              </div>
            )}

            <div className="mt-6 text-xs text-stone2-500 flex items-center gap-2">
              <ShieldCheck className="w-4 h-4 text-sage-700" />
              <span>
                Posted by <strong className="text-sage-900">{listing.parent_first_name || "a family"}</strong>.
                Photos and full profile stay private until the family replies to your message.
              </span>
            </div>
          </article>

          <aside className="paper-card p-5 h-fit lg:sticky lg:top-24" data-testid="listing-reach-out">
            <h2 className="font-heading font-semibold text-sage-900 text-lg">Reach out to {listing.parent_first_name || "the family"}</h2>
            <p className="text-xs text-stone2-500 mt-1">
              Introduce yourself briefly — a warm first message with your training and why you'd be a good fit goes far.
            </p>
            <textarea rows={6} value={msg} onChange={(e) => setMsg(e.target.value)}
              data-testid="listing-message-body" maxLength={2000}
              placeholder={`Hi ${listing.parent_first_name || "there"}, I saw your listing and I'd love to help. I'm an RBT with 4 years of experience...`}
              className="mt-3 w-full rounded-lg border border-stone2-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-sage-500" />
            <button type="button" onClick={sendMessage} disabled={sending}
              data-testid="listing-message-send" className="btn-primary w-full mt-3">
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageSquare className="w-4 h-4" />}
              Send message
            </button>
            <p className="mt-3 text-[11px] text-stone2-500 leading-relaxed">
              Messaging families is unlimited on your specialized-sitter account.
            </p>
          </aside>
        </div>
      </div>
    </div>
  );
}
